import React, { useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FaDownload, FaSearch } from 'react-icons/fa';
import MonitoringListSelect from '../FormComponents/MonitoringListSelect';
import {
  useDownloadRegistrationExportMutation,
  usePreviewRegistrationExportMutation
} from '../../mutations/plateRegistration.mutation';
import {
  RegistrationExportPreview,
  RegistrationExportRequest
} from '../../types/plateRegistration.types';
import { PlateClassification } from '../../Types';

const classificationOptions = [
  { value: PlateClassification.Unassigned, label: 'Unassigned' },
  { value: PlateClassification.Personal, label: 'Personal' },
  { value: PlateClassification.Business, label: 'Business' }
];

const formatSize = (bytes: number) => {
  if (!bytes) return '0 MB';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function RegistrationExport() {
  const [monitoringListId, setMonitoringListId] = useState('');
  const [classification, setClassification] = useState<PlateClassification | null>(null);
  const [includeArchivedPlates, setIncludeArchivedPlates] = useState(false);
  const [uploadedStartDate, setUploadedStartDate] = useState('');
  const [uploadedEndDate, setUploadedEndDate] = useState('');

  // Held separately from the live filter state so downloads always use exactly the
  // filters the preview was built from.
  const [preview, setPreview] = useState<RegistrationExportPreview | null>(null);
  const [previewedFilters, setPreviewedFilters] = useState<RegistrationExportRequest | null>(null);
  const [downloadingChunk, setDownloadingChunk] = useState<number | null>(null);

  const { mutate: buildPreview, isLoading: isPreviewing } = usePreviewRegistrationExportMutation();
  const { mutate: downloadChunk } = useDownloadRegistrationExportMutation();

  const buildFilters = (): RegistrationExportRequest => ({
    monitoringListId: monitoringListId || undefined,
    classification: classification ?? undefined,
    includeArchivedPlates,
    uploadedStartDate: uploadedStartDate || undefined,
    uploadedEndDate: uploadedEndDate || undefined
  });

  const handlePreview = () => {
    const filters = buildFilters();
    buildPreview(filters, {
      onSuccess: (data) => {
        setPreview(data ?? null);
        setPreviewedFilters(filters);
      }
    });
  };

  const handleDownload = (chunkIndex: number, fallbackFileName: string) => {
    if (!previewedFilters) return;
    setDownloadingChunk(chunkIndex);
    downloadChunk(
      { payload: previewedFilters, chunkIndex, fallbackFileName },
      { onSettled: () => setDownloadingChunk(null) }
    );
  };

  return (
    <div>
      <h4>Export Plate Registrations</h4>
      <p className="text-muted small">
        Downloads the current registration file for every matching plate as a ZIP, organized into a
        folder per client. Large exports are split into multiple parts.
      </p>

      <Row className="g-2 align-items-end mb-3">
        <Col md={3}>
          <Form.Label className="fw-bold small mb-1">Monitoring List</Form.Label>
          <MonitoringListSelect
            handleDataChange={(_key, value) => setMonitoringListId(value)}
            value={monitoringListId}
            addNew={false}
            showAllOption
          />
        </Col>

        <Col md={2}>
          <Form.Label className="fw-bold small mb-1">Classification</Form.Label>
          <Form.Select
            value={classification ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setClassification(value === '' ? null : (Number(value) as PlateClassification));
            }}>
            <option value="">All</option>
            {classificationOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Label className="fw-bold small mb-1">Uploaded From</Form.Label>
          <Form.Control
            type="date"
            value={uploadedStartDate}
            onChange={(e) => setUploadedStartDate(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <Form.Label className="fw-bold small mb-1">Uploaded To</Form.Label>
          <Form.Control
            type="date"
            value={uploadedEndDate}
            onChange={(e) => setUploadedEndDate(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Check
            type="checkbox"
            id="include-archived-plates"
            label="Include archived plates"
            checked={includeArchivedPlates}
            onChange={(e) => setIncludeArchivedPlates(e.target.checked)}
          />
          <Button
            variant="primary"
            size="sm"
            className="mt-2 d-flex align-items-center gap-2"
            disabled={isPreviewing}
            onClick={handlePreview}>
            {isPreviewing ? <Spinner animation="border" size="sm" /> : <FaSearch />}
            Preview Export
          </Button>
        </Col>
      </Row>

      {preview && (
        <>
          {preview.totalFiles === 0 ? (
            <Alert variant="warning">No registration files match those filters.</Alert>
          ) : (
            <>
              <Alert variant="info">
                <strong>{preview.totalFiles}</strong> file(s),{' '}
                <strong>{formatSize(preview.totalSizeInBytes)}</strong> total, in{' '}
                <strong>{preview.totalChunks}</strong> ZIP file(s).
              </Alert>

              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Part</th>
                    <th>Files</th>
                    <th>Size</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.chunks.map((chunk) => (
                    <tr key={chunk.chunkIndex}>
                      <td>{chunk.chunkIndex + 1}</td>
                      <td>{chunk.fileCount}</td>
                      <td>{formatSize(chunk.totalSizeInBytes)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={downloadingChunk !== null}
                          onClick={() => handleDownload(chunk.chunkIndex, chunk.fileName)}
                          className="d-flex align-items-center gap-2">
                          {downloadingChunk === chunk.chunkIndex ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaDownload />
                          )}
                          {chunk.fileName}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default RegistrationExport;
